import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import {
  createI18n,
  ADMIN_LOCALES,
  DEFAULT_LOCALE,
  isSupportedLocale,
  type SupportedLocale,
} from '@noxverify/i18n';
import { App } from './App';
import { eagerEnResources, attachLazyLocaleLoader } from './i18n-resources';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './styles.css';

// ---------------------------------------------------------------------------
// Admin locale resources (6 ADMIN_LOCALES, namespaces `common` + `admin`).
// English is bundled eagerly (default + fallbackLng); the other 5 locales are
// code-split and lazy-loaded on demand by `attachLazyLocaleLoader`, keeping
// their JSON out of the main chunk.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Resolve initial locale from localStorage -> navigator -> default
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'nv_locale_admin';

function resolveInitialLocale(): SupportedLocale {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && isSupportedLocale(stored) && ADMIN_LOCALES.includes(stored)) {
        return stored;
      }
    } catch {
      // localStorage access denied (private mode, etc.) — fall through.
    }

    const navLang = window.navigator?.language ?? '';
    // Try exact match first, then language prefix (e.g., "de-AT" -> "de").
    if (isSupportedLocale(navLang) && ADMIN_LOCALES.includes(navLang)) {
      return navLang;
    }
    const prefix = navLang.split('-')[0];
    if (prefix && isSupportedLocale(prefix) && ADMIN_LOCALES.includes(prefix as SupportedLocale)) {
      return prefix as SupportedLocale;
    }
  }
  return DEFAULT_LOCALE;
}

const initialLocale = resolveInitialLocale();

const i18n = createI18n({
  locale: initialLocale,
  resources: eagerEnResources,
  useHttpBackend: false,
  detectLanguage: false,
  ns: ['common', 'admin'],
  defaultNS: 'common',
  supportedLngs: ADMIN_LOCALES,
});

// Lazy-load the resolved locale (and any future language switch) on demand.
attachLazyLocaleLoader(i18n);

// Persist locale on language changes so the choice survives reload.
i18n.on('languageChanged', (lng) => {
  if (typeof window === 'undefined') return;
  try {
    if (isSupportedLocale(lng) && ADMIN_LOCALES.includes(lng)) {
      window.localStorage.setItem(STORAGE_KEY, lng);
    }
  } catch {
    // Ignore storage write failures.
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </I18nextProvider>
  </React.StrictMode>,
);
